"use client";

import { useEffect, useMemo, useRef } from "react";
import Script from "next/script";

import { identifyGAUser, identifyPostHogUser } from "@traqly/analytics/client";
import { getUtmCookieValues } from "@traqly/analytics/utm/client";
import { UTM_TO_GA_CAMPAIGN } from "@traqly/analytics/utm/constants";

import { createClient } from "@lib/utils/supabase/client";

export function Analytics() {
  const isMountedRef = useRef(true);
  const supabase = useMemo(() => createClient(), []);

  const gaId = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID;
  const utm = getUtmCookieValues();
  const campaignData: Record<string, string> = {};

  for (const [utmKey, gaKey] of Object.entries(UTM_TO_GA_CAMPAIGN)) {
    const value = utm[utmKey as keyof typeof utm];
    if (value) campaignData[gaKey] = value;
  }

  const gtagConfig = {
    page_location: typeof window !== "undefined" ? window.location.href : "",
    ...(campaignData || {}),
  };
  // Sanitize the config to prevent XSS attacks
  const safeConfig = JSON.stringify(gtagConfig)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");

  useEffect(() => {
    isMountedRef.current = true;

    const getData = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user || !isMountedRef.current) return;

        const { data, error } = await supabase
          .from("users")
          .select(
            `id,
          first_name,
          last_name,
          email,
          business_type,
          other_payment_methods,
          stripe_user_id,
          subscriptions (plan_tier, billing_period, status)
          `,
          )
          .eq("id", user.id)
          .maybeSingle();

        if (!isMountedRef.current) return;

        if (error) {
          return;
        }

        const basePayload = {
          business_type: data?.business_type,
          other_payment_methods: data?.other_payment_methods,
          has_stripe_account: !!data?.stripe_user_id,
          sub_plan_tier: data?.subscriptions?.[0]?.plan_tier,
          sub_billing_period: data?.subscriptions?.[0]?.billing_period,
          sub_status: data?.subscriptions?.[0]?.status,
        };

        identifyPostHogUser(user.id, {
          ...basePayload,
          email: data?.email,
          first_name: data?.first_name,
          last_name: data?.last_name,
        });
        identifyGAUser(user.id, basePayload);
      } catch {
        // Silently fail
      }
    };

    getData();

    return () => {
      isMountedRef.current = false;
    };
  }, [supabase]);

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${gaId}`}
        strategy="afterInteractive"
      />

      <Script
        id="ga-init"
        strategy="afterInteractive"
      >
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());

          const configParams = ${safeConfig};
          
          if (configParams.source) {
            gtag('set', 'campaign', {
              source: configParams.source,
              medium: configParams.medium,
              campaign: configParams.campaign,
              term: configParams.term,
              content: configParams.content,
            });
          }

          gtag('config', '${gaId}', configParams);
        `}
      </Script>
    </>
  );
}
